import React, { useEffect, useState } from "react";
import {
  Briefcase,
  DollarSign,
  Heart,
  Mail,
  TrendingUp,
  Users,
  Repeat,
  FileText,
  Sparkles,
  Info,
  CheckCircle2,
} from "lucide-react";
import api from "../../lib/api";
import { useT } from "../../context/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: v % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

// Tone → background + icon color classes (kept identical to original
// design so the card surfaces still read the same).
const TONE = {
  green: "bg-emerald-50 text-emerald-800 border-emerald-100",
  blue: "bg-blue-50 text-blue-700 border-blue-100",
  amber: "bg-amber-50 text-amber-800 border-amber-100",
  gray: "bg-gray-100 text-gray-700 border-gray-200",
  rose: "bg-rose-50 text-rose-700 border-rose-100",
};

// Renders a single metric card. When `onClick` is provided the whole card
// becomes a real <button> so it's keyboard- and screen-reader-accessible,
// and gets hover/focus affordances that signal interactivity.
const MetricCard = ({
  icon: Icon,
  label,
  value,
  hint,
  tone = "gray",
  testid,
  onClick,
}) => {
  const inner = (
    <>
      <span
        className={`w-9 h-9 rounded-lg flex items-center justify-center border ${TONE[tone]}`}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center gap-1">
          <span className="truncate">{label}</span>
          {onClick ? (
            <Info
              className="w-3 h-3 text-gray-400 group-hover:text-emerald-700 transition-colors shrink-0"
              aria-hidden="true"
            />
          ) : null}
        </div>
        <div className="font-display text-xl sm:text-2xl font-semibold text-gray-900 leading-tight mt-0.5 truncate">
          {value ?? "—"}
        </div>
        {hint ? (
          <div className="mt-0.5 text-[11px] text-gray-500 leading-tight">
            {hint}
          </div>
        ) : null}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group rounded-xl border border-gray-200 bg-white p-4 flex items-start gap-3 text-left w-full cursor-pointer transition-all duration-150 hover:border-emerald-300 hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 active:translate-y-0"
        data-testid={testid}
      >
        {inner}
      </button>
    );
  }
  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-4 flex items-start gap-3"
      data-testid={testid}
    >
      {inner}
    </div>
  );
};

export const BusinessInsights = () => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // When set, this card's "Learn more" modal is open. Single source of
  // truth keeps the dialog mounted once at the section level.
  const [activeCard, setActiveCard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/dashboard/business-insights");
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Couldn't load insights.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Render skeleton on first load — no scary all-zero numbers before data
  // arrives. Hide entirely on auth error (gracefully degrade, since this is
  // not a critical surface).
  if (loading) {
    return (
      <section
        className="mt-6 rounded-2xl bg-white border border-gray-200 overflow-hidden"
        data-testid="business-insights-loading"
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <div className="h-7 w-40 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-20 rounded-xl border border-gray-200 bg-gray-50 animate-pulse"
            />
          ))}
        </div>
      </section>
    );
  }
  if (error || !data) {
    // Quietly hide on error — not critical to the main dashboard experience.
    return null;
  }

  const cm = data.current_month || {};
  const l30 = data.last_30_days || {};
  const derived = data.derived || {};
  const pipeline = data.pipeline || {};
  const completionPct =
    derived.completion_rate == null
      ? null
      : Math.round((derived.completion_rate || 0) * 100);

  // "Have we seen any activity yet?" — controls empty-state hint copy.
  const hasAnyBookings = (cm.booked_jobs || 0) > 0;
  const hasReturning = (cm.returning_customers || 0) > 0;
  const isEmpty =
    !hasAnyBookings &&
    !hasReturning &&
    !cm.review_requests_sent &&
    !l30.leads_created &&
    !l30.quotes_sent &&
    !l30.rebook_reminders_sent;

  // Card → modal payload. Keeps the label + display value the user just
  // clicked, the i18n keys for the explanation + tip, and a stable testid.
  // Declared after data is available so `value` reflects the real number.
  const CARD_INFO = {
    bookedJobs: {
      labelKey: "bi.bookedJobs",
      value: String(cm.booked_jobs ?? 0),
      explainKey: "bi.bookedJobs.explain",
      tipKey: "bi.bookedJobs.tip",
    },
    estimatedRevenue: {
      labelKey: "bi.estimatedRevenue",
      value: fmtMoney(cm.estimated_revenue || 0),
      explainKey: "bi.estimatedRevenue.explain",
      tipKey: "bi.estimatedRevenue.tip",
    },
    returningCustomers: {
      labelKey: "bi.returningCustomers",
      value: String(cm.returning_customers ?? 0),
      explainKey: "bi.returningCustomers.explain",
      tipKey: "bi.returningCustomers.tip",
    },
    reviewsSent: {
      labelKey: "bi.reviewsSent",
      value: String(cm.review_requests_sent ?? 0),
      explainKey: "bi.reviewsSent.explain",
      tipKey: "bi.reviewsSent.tip",
    },
    leadsCreated: {
      labelKey: "bi.leadsCreated",
      value: String(l30.leads_created ?? 0),
      explainKey: "bi.leadsCreated.explain",
      tipKey: "bi.leadsCreated.tip",
    },
    quotesSent: {
      labelKey: "bi.quotesSent",
      value: String(l30.quotes_sent ?? 0),
      explainKey: "bi.quotesSent.explain",
      tipKey: "bi.quotesSent.tip",
    },
    rebooksSent: {
      labelKey: "bi.rebooksSent",
      value: String(l30.rebook_reminders_sent ?? 0),
      explainKey: "bi.rebooksSent.explain",
      tipKey: "bi.rebooksSent.tip",
    },
    returningLeads: {
      labelKey: "bi.returningLeads",
      value: String(l30.returning_customer_leads ?? 0),
      explainKey: "bi.returningLeads.explain",
      tipKey: "bi.returningLeads.tip",
    },
    // ---- New CRM-health / Pipeline cards (Phase 1 polish) ----
    closedJobs: {
      labelKey: "bi.closedJobs",
      value: String(cm.completed_jobs ?? 0),
      explainKey: "bi.closedJobs.explain",
      tipKey: "bi.closedJobs.tip",
    },
    potentialRevenue: {
      labelKey: "bi.potentialRevenue",
      value: fmtMoney(pipeline.potential_revenue || 0),
      explainKey: "bi.potentialRevenue.explain",
      tipKey: "bi.potentialRevenue.tip",
    },
    cancelledJobs: {
      labelKey: "bi.cancelledJobs",
      value: String(cm.cancelled_jobs ?? 0),
      explainKey: "bi.cancelledJobs.explain",
      tipKey: "bi.cancelledJobs.tip",
    },
    lostRevenue: {
      labelKey: "bi.lostRevenue",
      value: fmtMoney(cm.cancelled_revenue || 0),
      explainKey: "bi.lostRevenue.explain",
      tipKey: "bi.lostRevenue.tip",
    },
    completionRate: {
      labelKey: "bi.completionRate",
      value: completionPct == null ? "—" : `${completionPct}%`,
      explainKey: "bi.completionRate.explain",
      tipKey: "bi.completionRate.tip",
    },
  };

  const activeInfo = activeCard ? CARD_INFO[activeCard] : null;

  return (
    <section
      className="mt-6 rounded-2xl bg-white border border-gray-200 overflow-hidden"
      data-testid="business-insights"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-emerald-700" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-gray-900">
              {t("bi.title")}
            </h2>
            <p
              className="text-xs text-gray-500"
              data-testid="business-insights-estimate-note"
            >
              {t("bi.subtitle")} · {t("bi.thisMonth")} ({cm.month_label}) · {t("bi.excludesNoValue")}
            </p>
          </div>
        </div>
        {hasAnyBookings ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 text-[11px] font-semibold"
            data-testid="business-insights-trend"
          >
            <TrendingUp className="w-3 h-3" />
            {t("bi.bookingsLive")}
          </span>
        ) : null}
      </header>

      {isEmpty ? (
        <div
          className="p-8 text-center"
          data-testid="business-insights-empty"
        >
          <span className="inline-flex w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 items-center justify-center mb-3">
            <Sparkles className="w-5 h-5 text-emerald-700" />
          </span>
          <h3 className="font-display text-base font-semibold text-gray-900">
            {t("bi.emptyTitle")}
          </h3>
          <p className="mt-1.5 text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
            {t("bi.emptyBody")}
          </p>
        </div>
      ) : (
        <>
          {/* This-month section */}
          <div className="px-5 pt-4">
            <h3 className="font-display text-xs font-semibold text-gray-700 uppercase tracking-wider">
              {t("bi.thisMonth")}
            </h3>
          </div>
          <div className="px-5 pt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard
              icon={Briefcase}
              label={t("bi.bookedJobs")}
              value={cm.booked_jobs ?? 0}
              tone="green"
              testid="bi-card-booked-jobs"
              onClick={() => setActiveCard("bookedJobs")}
            />
            <MetricCard
              icon={CheckCircle2}
              label={t("bi.closedJobs")}
              value={cm.completed_jobs ?? 0}
              tone="green"
              testid="bi-card-closed-jobs"
              onClick={() => setActiveCard("closedJobs")}
            />
            <MetricCard
              icon={DollarSign}
              label={t("bi.estimatedRevenue")}
              value={fmtMoney(cm.estimated_revenue || 0)}
              hint={t("bi.revenueHint")}
              tone="green"
              testid="bi-card-revenue"
              onClick={() => setActiveCard("estimatedRevenue")}
            />
            <MetricCard
              icon={Heart}
              label={t("bi.returningCustomers")}
              value={cm.returning_customers ?? 0}
              hint={
                hasReturning ? t("bi.returningPositive") : undefined
              }
              tone={hasReturning ? "rose" : "gray"}
              testid="bi-card-returning-customers"
              onClick={() => setActiveCard("returningCustomers")}
            />
            <MetricCard
              icon={Mail}
              label={t("bi.reviewsSent")}
              value={cm.review_requests_sent ?? 0}
              tone="amber"
              testid="bi-card-reviews-sent"
              onClick={() => setActiveCard("reviewsSent")}
            />
          </div>

          {/* 30-day section */}
          <div className="px-5 pt-5">
            <h3 className="font-display text-xs font-semibold text-gray-700 uppercase tracking-wider">
              {t("bi.last30Days")}
            </h3>
          </div>
          <div className="px-5 py-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard
              icon={Users}
              label={t("bi.leadsCreated")}
              value={l30.leads_created ?? 0}
              tone="blue"
              testid="bi-card-30d-leads"
              onClick={() => setActiveCard("leadsCreated")}
            />
            <MetricCard
              icon={FileText}
              label={t("bi.quotesSent")}
              value={l30.quotes_sent ?? 0}
              tone="blue"
              testid="bi-card-30d-quotes"
              onClick={() => setActiveCard("quotesSent")}
            />
            <MetricCard
              icon={Repeat}
              label={t("bi.rebooksSent")}
              value={l30.rebook_reminders_sent ?? 0}
              tone="amber"
              testid="bi-card-30d-rebooks"
              onClick={() => setActiveCard("rebooksSent")}
            />
            <MetricCard
              icon={Heart}
              label={t("bi.returningLeads")}
              value={l30.returning_customer_leads ?? 0}
              tone="rose"
              testid="bi-card-30d-returning-leads"
              onClick={() => setActiveCard("returningLeads")}
            />
          </div>

          {/* CRM health & pipeline integrity — only renders when there's
              actually something to show. Keeps the dashboard clean for
              brand-new accounts (no cancelled jobs / no quotes yet). */}
          {(cm.cancelled_jobs > 0 ||
            derived.completion_rate != null ||
            derived.booking_conversion_rate != null ||
            (data?.pipeline?.potential_revenue || 0) > 0) ? (
            <>
              <div className="px-5 pt-5">
                <h3 className="font-display text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  {t("bi.pipelineHealth")}
                </h3>
              </div>
              <div className="px-5 pt-2 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bi-pipeline-health">
                <MetricCard
                  icon={DollarSign}
                  label={t("bi.potentialRevenue")}
                  value={fmtMoney(data?.pipeline?.potential_revenue || 0)}
                  hint={t("bi.potentialRevenueHint")}
                  tone="blue"
                  testid="bi-card-potential-revenue"
                  onClick={() => setActiveCard("potentialRevenue")}
                />
                <MetricCard
                  icon={Briefcase}
                  label={t("bi.cancelledJobs")}
                  value={cm.cancelled_jobs ?? 0}
                  tone={(cm.cancelled_jobs || 0) > 0 ? "rose" : "gray"}
                  testid="bi-card-cancelled-jobs"
                  onClick={() => setActiveCard("cancelledJobs")}
                />
                <MetricCard
                  icon={DollarSign}
                  label={t("bi.lostRevenue")}
                  value={fmtMoney(cm.cancelled_revenue || 0)}
                  tone={(cm.cancelled_revenue || 0) > 0 ? "rose" : "gray"}
                  testid="bi-card-lost-revenue"
                  onClick={() => setActiveCard("lostRevenue")}
                />
                <MetricCard
                  icon={CheckCircle2}
                  label={t("bi.completionRate")}
                  value={
                    derived.completion_rate == null
                      ? "—"
                      : `${Math.round((derived.completion_rate || 0) * 100)}%`
                  }
                  tone="green"
                  testid="bi-card-completion-rate"
                  onClick={() => setActiveCard("completionRate")}
                />
              </div>
            </>
          ) : null}

          {/* Derived metrics (footer strip) */}
          {(derived.average_job_value > 0 ||
            derived.estimated_returning_revenue > 0) ? (
            <div className="mx-5 my-4 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]">
              {derived.average_job_value > 0 ? (
                <span data-testid="bi-derived-avg">
                  <span className="text-gray-500">{t("bi.averageJobValue")}:</span>{" "}
                  <span className="font-semibold text-gray-900">
                    {fmtMoney(derived.average_job_value)}
                  </span>
                </span>
              ) : null}
              {derived.estimated_returning_revenue > 0 ? (
                <span data-testid="bi-derived-returning-rev">
                  <span className="text-gray-500">{t("bi.estimatedReturningRevenue")}:</span>{" "}
                  <span className="font-semibold text-gray-900">
                    {fmtMoney(derived.estimated_returning_revenue)}
                  </span>
                </span>
              ) : null}
              <span className="text-[11px] text-gray-500 ml-auto italic">
                {t("bi.allEstimates")}
              </span>
            </div>
          ) : (
            <div className="px-5 pb-4 text-[11px] text-gray-400 italic">
              {t("bi.allEstimates")}
            </div>
          )}
        </>
      )}

      {/* "Learn more" modal — single instance, swaps content via activeCard. */}
      <Dialog
        open={Boolean(activeInfo)}
        onOpenChange={(o) => { if (!o) setActiveCard(null); }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="bi-card-modal"
        >
          {activeInfo ? (
            <>
              <DialogHeader>
                <DialogTitle
                  className="font-display text-xl text-gray-900"
                  data-testid="bi-card-modal-title"
                >
                  {t(activeInfo.labelKey)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 pt-1">
                <div
                  className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-3"
                  data-testid="bi-card-modal-value"
                >
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800">
                    {t("bi.modal.currentValue")}
                  </p>
                  <p className="font-display text-2xl font-bold text-emerald-900 mt-0.5 leading-tight">
                    {activeInfo.value}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                    {t("bi.modal.explain")}
                  </p>
                  <p
                    className="mt-1 text-sm text-gray-700 leading-relaxed"
                    data-testid="bi-card-modal-explain"
                  >
                    {t(activeInfo.explainKey)}
                  </p>
                </div>

                <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">
                    {t("bi.modal.tip")}
                  </p>
                  <p
                    className="mt-1 text-sm text-amber-900 leading-relaxed"
                    data-testid="bi-card-modal-tip"
                  >
                    {t(activeInfo.tipKey)}
                  </p>
                </div>
              </div>

              <DialogFooter className="mt-2">
                <Button
                  type="button"
                  onClick={() => setActiveCard(null)}
                  className="bg-green-800 hover:bg-green-900 text-white"
                  data-testid="bi-card-modal-close"
                >
                  {t("bi.modal.close")}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
};
